// Rasterize the first page of a PDF File to a PNG data URL.
// pdfjs-dist is large; load it (and its worker) only when the user actually
// uploads a PDF.

const MAX_EDGE_PX = 2048;
const TARGET_DPI = 200;

export async function rasterizePdfFirstPage(file: File): Promise<string> {
  const [{ getDocument, GlobalWorkerOptions }, workerUrl] = await Promise.all([
    import("pdfjs-dist"),
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]);
  GlobalWorkerOptions.workerSrc = workerUrl.default;

  const buf = await file.arrayBuffer();
  const pdf = await getDocument({ data: buf }).promise;
  try {
    const page = await pdf.getPage(1);

    // pdf.js viewport at scale 1 is in 72 DPI points. Scale to TARGET_DPI,
    // then clamp so the longest edge does not exceed MAX_EDGE_PX.
    const base = page.getViewport({ scale: 1 });
    let scale = TARGET_DPI / 72;
    const longestAtScale = Math.max(base.width, base.height) * scale;
    if (longestAtScale > MAX_EDGE_PX) {
      scale *= MAX_EDGE_PX / longestAtScale;
    }
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement("canvas");
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas 2d context unavailable");

    // Fill white so transparent PDF backgrounds don't render as black.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    return canvas.toDataURL("image/png");
  } finally {
    await pdf.destroy();
  }
}
